import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useAuditHistory } from '../hooks/useAuditHistory';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface AuditHistoryViewerProps {
  userId: number | null;
}

const ACTION_TYPE_LABELS: Record<string, string> = {
  CommissionCreated: 'Commission Created',
  DrawRecorded: 'Draw Recorded',
  CommissionAdjusted: 'Commission Adjusted',
  ReconcileDateSet: 'Reconcile Date Set',
};

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString();
}

export function AuditHistoryViewer({ userId }: AuditHistoryViewerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data: entries, isLoading, error } = useAuditHistory(
    isExpanded ? userId : null,
    startDate || undefined,
    endDate || undefined
  );

  if (!userId) return null;

  return (
    <div className="border rounded-lg">
      <button
        type="button"
        className="flex items-center gap-2 w-full p-4 text-left font-medium hover:bg-gray-50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        Audit History
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          <div className="flex gap-4 items-end">
            <div>
              <Label htmlFor="audit-start-date">Start Date</Label>
              <Input
                id="audit-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-auto"
              />
            </div>
            <div>
              <Label htmlFor="audit-end-date">End Date</Label>
              <Input
                id="audit-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-auto"
              />
            </div>
          </div>

          {error ? (
            <p className="text-red-500 py-4">Error: {error}</p>
          ) : isLoading ? (
            <p className="text-gray-500 py-4">Loading audit history...</p>
          ) : entries.length === 0 ? (
            <p className="text-gray-500 py-4">No audit records found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 pr-4">Date/Time</th>
                    <th className="pb-2 pr-4">Action</th>
                    <th className="pb-2 pr-4">Field Changed</th>
                    <th className="pb-2 pr-4">Old Value</th>
                    <th className="pb-2 pr-4">New Value</th>
                    <th className="pb-2">Modified By</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id} className="border-b last:border-b-0">
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {formatDateTime(entry.modifiedAt)}
                      </td>
                      <td className="py-2 pr-4">
                        {ACTION_TYPE_LABELS[entry.actionType] ?? entry.actionType}
                      </td>
                      <td className="py-2 pr-4">{entry.fieldChanged}</td>
                      <td className="py-2 pr-4 text-gray-500">
                        {entry.oldValue ?? '—'}
                      </td>
                      <td className="py-2 pr-4">{entry.newValue ?? '—'}</td>
                      <td className="py-2">{entry.modifiedByName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
